import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';
import { MessageCircle, Inbox, Send, FileText, Clock, Users, CheckCircle2 } from 'lucide-react';

export default function AdminTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [replying, setReplying] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const { data } = await axios.get(`/api/admin/tickets?${params.toString()}`);
      setTickets(data.tickets || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [statusFilter]);

  const openTicket = async (ticket) => {
    setSelectedTicket(null);
    try {
      const { data } = await axios.get(`/api/admin/tickets/${ticket._id}`);
      setSelectedTicket(data.ticket);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load ticket details');
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!message.trim()) return toast.error('Reply message is required.');
    if (!selectedTicket) return;
    setReplying(true);
    try {
      const { data } = await axios.post(`/api/admin/tickets/${selectedTicket._id}/reply`, { message });
      toast.success('Reply sent successfully.');
      setSelectedTicket(data.ticket);
      setMessage('');
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reply failed');
    } finally {
      setReplying(false);
    }
  };

  const changeStatus = async (newStatus) => {
    if (!selectedTicket) return;
    try {
      const { data } = await axios.patch(`/api/admin/tickets/${selectedTicket._id}/status`, { status: newStatus });
      setSelectedTicket(data.ticket);
      toast.success(`Ticket ${newStatus}.`);
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to update status');
    }
  };

  const renderStatus = (status) => {
    const mapping = {
      open: { label: 'Open', className: 'bg-primary-500/20 text-primary-200' },
      pending: { label: 'Pending', className: 'bg-amber-500/20 text-amber-200' },
      closed: { label: 'Closed', className: 'bg-gray-600/20 text-gray-200' },
    };
    return <span className={`text-xs px-2 py-1 rounded-full ${mapping[status]?.className || 'bg-gray-600/20 text-gray-200'}`}>{mapping[status]?.label || status}</span>;
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-6">
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-5 h-5 text-red-400" />
              <div>
                <h1 className="text-2xl font-bold text-white">Support Tickets</h1>
                <p className="text-gray-400 text-sm">Manage all user support tickets and reply directly.</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-gray-400">Filter by status:</div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-dark-600 border border-white/10 text-white rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-red-400">
                <option value="">All Tickets</option>
                <option value="open">Open</option>
                <option value="pending">Pending</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6 overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-300">
              <thead>
                <tr className="text-gray-400 uppercase text-xs tracking-[0.12em]">
                  <th className="pb-3">User</th>
                  <th className="pb-3">Subject</th>
                  <th className="pb-3">Status</th>
                  <th className="pb-3">Updated</th>
                  <th className="pb-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="py-10 text-center text-gray-500">Loading tickets...</td></tr>
                ) : tickets.length === 0 ? (
                  <tr><td colSpan={5} className="py-10 text-center text-gray-500">No tickets found.</td></tr>
                ) : tickets.map((ticket) => (
                  <tr key={ticket._id} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-3 pr-4 text-white">{ticket.user?.name || 'Unknown'}</td>
                    <td className="py-3 pr-4 text-white">{ticket.subject}</td>
                    <td className="py-3 pr-4">{renderStatus(ticket.status)}</td>
                    <td className="py-3 pr-4 text-gray-400">{new Date(ticket.updatedAt).toLocaleString()}</td>
                    <td className="py-3">
                      <button onClick={() => openTicket(ticket)}
                        className="text-sm text-red-300 hover:text-white">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Inbox className="w-5 h-5 text-primary-400" />
              <div>
                <h2 className="text-xl font-semibold text-white">Ticket Conversation</h2>
                <p className="text-gray-400 text-sm">View ticket details and send a reply.</p>
              </div>
            </div>

            {!selectedTicket ? (
              <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-gray-400">
                Select a ticket to open the conversation.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-2xl bg-dark-800 border border-white/10 p-5">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{selectedTicket.subject}</h3>
                      <div className="flex flex-wrap gap-2 mt-2 text-sm text-gray-400">
                        <span>{selectedTicket.user?.name} • {selectedTicket.user?.email}</span>
                        <span>{renderStatus(selectedTicket.status)}</span>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">Updated {new Date(selectedTicket.updatedAt).toLocaleString()}</span>
                  </div>

                  <div className="space-y-3">
                    {selectedTicket.messages.map((msg) => (
                      <div key={msg._id} className={`rounded-2xl p-4 ${msg.role === 'admin' ? 'bg-red-500/10 border border-red-500/10' : 'bg-primary-500/10 border border-primary-500/10'}`}>
                        <div className="flex items-center justify-between gap-3 mb-2 text-sm text-gray-300">
                          <span className="font-medium text-white">{msg.role === 'admin' ? 'Admin reply' : msg.sender?.name || 'User'}</span>
                          <span>{new Date(msg.createdAt).toLocaleString()}</span>
                        </div>
                        <p className="text-gray-200 whitespace-pre-line">{msg.message}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => changeStatus('open')}
                      className="bg-primary-600 hover:bg-primary-500 text-white rounded-2xl px-4 py-2 text-sm">Set Open</button>
                    <button onClick={() => changeStatus('pending')}
                      className="bg-amber-500 hover:bg-amber-400 text-dark-900 rounded-2xl px-4 py-2 text-sm">Set Pending</button>
                    <button onClick={() => changeStatus('closed')}
                      className="bg-gray-600 hover:bg-gray-500 text-white rounded-2xl px-4 py-2 text-sm">Close Ticket</button>
                  </div>

                  <form onSubmit={handleReply} className="space-y-4">
                    <textarea
                      rows={4}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full bg-dark-600 border border-white/10 text-white rounded-2xl px-4 py-3 focus:outline-none focus:border-red-400"
                      placeholder="Write a response for the user"
                    />
                    <button type="submit" disabled={replying}
                      className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-400 disabled:bg-red-700 text-white rounded-2xl px-5 py-3 font-semibold transition-colors">
                      {replying ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send className="w-4 h-4" />} Send Reply
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
